/**
 * WheelManager — Continuous wheel spin and discrete steering for automotive models.
 *
 * Responsibilities:
 *   - Traverse the scene on init() to find rotation and steering nodes by name
 *   - Spin rotation nodes continuously around their local X-axis with ease-in/out
 *   - Lerp steering nodes to discrete Y-angle positions (left / straight / right)
 *   - Integrate with the demand frameloop via onNeedsRender callback
 *
 * Lifecycle: init() → [tick() per frame] → dispose()
 *
 * @see docs/ims-engine-api/01-architecture.md
 */

import type { Object3D } from 'three';
import type {
  WheelConfig,
  WheelState,
  SteeringPosition,
  WheelChangedEvent,
} from '../api/types/wheel';
import type { RevealObject } from './RevealController';
import { devWarn } from '../utils/devLog';

// ---------------------------------------------------------------------------
// Constants & Helpers
// ---------------------------------------------------------------------------

const DEG_TO_RAD = Math.PI / 180;
const DEFAULT_ROTATION_SPEED = Math.PI * 2; // 1 revolution/sec
const DEFAULT_STEERING_ANGLE_DEG = 30;
const DEFAULT_ROTATION_EASE_MS = 800;
const DEFAULT_STEERING_DURATION_MS = 600;

/** Smooth cubic ease-in/out, t in [0, 1]. */
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Move `current` towards `target` by at most `step` per call. Never overshoots. */
function moveTowards(current: number, target: number, step: number): number {
  if (Math.abs(target - current) <= step) return target;
  return current + Math.sign(target - current) * step;
}

/** Linear interpolation. */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// ---------------------------------------------------------------------------
// WheelManager
// ---------------------------------------------------------------------------

/**
 * Manages wheel spin and steering for a loaded GLB scene.
 *
 * Instantiated by createEngine, initialised once the model mounts.
 */
export class WheelManager {
  // ── Node references ──────────────────────────────────────────────────────
  private _rotationNodes: Object3D[] = [];
  private _steeringNodes: Object3D[] = [];
  /** Node names to search for — stored so additional scenes can be scanned after init. */
  private _steeringNodeNames: string[] = [];

  // ── Merged config (with defaults applied) ────────────────────────────────
  private _rotationSpeed = DEFAULT_ROTATION_SPEED;
  private _steeringAngleRad = DEFAULT_STEERING_ANGLE_DEG * DEG_TO_RAD;
  private _rotationEaseSpeed = 1 / (DEFAULT_ROTATION_EASE_MS / 1000); // factor/sec
  private _steeringDurationSec = DEFAULT_STEERING_DURATION_MS / 1000;

  // ── Rotation state ───────────────────────────────────────────────────────
  private _isRotating = false;
  private _currentSpeedFactor = 0; // 0..1 (eased)
  private _targetSpeedFactor = 0;

  // ── Steering state ───────────────────────────────────────────────────────
  private _steeringPosition: SteeringPosition = 'straight';
  private _currentAngleRad = 0;
  private _targetAngleRad = 0;
  private _steeringStartAngle = 0;
  private _steeringProgress = 1; // 1 = done, 0 = just started

  // ── Lifecycle ────────────────────────────────────────────────────────────
  private _initialized = false;

  // ── Callbacks (wired by createEngine / EngineComponents) ─────────────────

  /** Called whenever a render is needed (wired to R3F `invalidate`). */
  onNeedsRender?: () => void;

  /** Called whenever rotation or steering state changes. */
  onChanged?: (event: WheelChangedEvent) => void;

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Traverse the scene and collect the named nodes.
   * Config defaults are merged so the API caller only needs to specify node names.
   */
  init(scene: Object3D, config: WheelConfig): void {
    // Merge config with defaults
    this._rotationSpeed = config.rotationSpeed ?? DEFAULT_ROTATION_SPEED;
    this._steeringAngleRad = (config.steeringAngleDeg ?? DEFAULT_STEERING_ANGLE_DEG) * DEG_TO_RAD;
    this._rotationEaseSpeed =
      1 / ((config.rotationEaseDurationMs ?? DEFAULT_ROTATION_EASE_MS) / 1000);
    this._steeringDurationSec = (config.steeringDurationMs ?? DEFAULT_STEERING_DURATION_MS) / 1000;

    // Store steering node names for later re-scan on external GLBs
    this._steeringNodeNames = [...(config.steeringNodes ?? [])];

    // Collect requested node names
    const rotSet = new Set(config.rotationNodes);
    const steerSet = new Set(config.steeringNodes);
    const foundRot = new Set<string>();
    const foundSteer = new Set<string>();

    // Single traversal — find all targets in one pass
    scene.traverse((obj) => {
      if (rotSet.has(obj.name)) {
        this._rotationNodes.push(obj);
        foundRot.add(obj.name);
      }
      if (steerSet.has(obj.name)) {
        this._steeringNodes.push(obj);
        foundSteer.add(obj.name);
      }
    });

    // Warn about missing rotation nodes
    for (const name of rotSet) {
      if (!foundRot.has(name)) {
        devWarn('wheelManager.init()', `Rotation node "${name}" not found in scene.`);
      }
    }

    // Warn about missing steering nodes
    for (const name of steerSet) {
      if (!foundSteer.has(name)) {
        devWarn('wheelManager.init()', `Steering node "${name}" not found in scene.`);
      }
    }

    this._initialized = true;
  }

  /**
   * Called every frame by WheelManagerTicker.
   * Advances rotation speed ramp and steering lerp.
   *
   * @param deltaSec - Time since last frame in seconds (from R3F useFrame delta).
   */
  tick(deltaSec: number): void {
    if (!this._initialized) return;

    // Clamp delta to prevent demand-mode spike: after idle the first delta equals
    // the full idle duration (e.g. 3 s), which would complete the 600 ms steering
    // animation instantly. Same guard as AnimationManager.MAX_TICK_DELTA.
    const dt = Math.min(deltaSec, 1 / 30);
    let needsRender = false;

    // ── Rotation speed ramp ──────────────────────────────────────────────
    if (this._currentSpeedFactor !== this._targetSpeedFactor) {
      this._currentSpeedFactor = moveTowards(
        this._currentSpeedFactor,
        this._targetSpeedFactor,
        this._rotationEaseSpeed * dt,
      );
      needsRender = true;
    }

    // ── Apply spin to rotation nodes ────────────────────────────────────
    if (this._currentSpeedFactor > 0) {
      const angleStep = this._rotationSpeed * this._currentSpeedFactor * dt;
      for (const node of this._rotationNodes) {
        node.rotation.z += angleStep;
      }
      needsRender = true;
    }

    // ── Steering lerp ────────────────────────────────────────────────────
    if (this._steeringProgress < 1) {
      this._steeringProgress = Math.min(1, this._steeringProgress + dt / this._steeringDurationSec);
      const t = easeInOutCubic(this._steeringProgress);
      this._currentAngleRad = lerp(this._steeringStartAngle, this._targetAngleRad, t);

      for (const node of this._steeringNodes) {
        node.rotation.y = this._currentAngleRad;
      }
      needsRender = true;
    }

    if (needsRender) {
      this.onNeedsRender?.();
    }
  }

  /**
   * Start or stop wheel spinning.
   *
   * @param active   - true = spin, false = stop
   * @param animated - true (default) = ease-in/out; false = instant
   */
  setRotation(active: boolean, animated = true): void {
    this._isRotating = active;
    this._targetSpeedFactor = active ? 1.0 : 0.0;

    if (!animated) {
      this._currentSpeedFactor = this._targetSpeedFactor;
      // Apply zero speed immediately (rotation.x keeps its current value — correct)
    }

    this.onChanged?.({ type: 'rotation', isRotating: active, animated });
    this.onNeedsRender?.();
  }

  /**
   * Steer front wheels to an exact angle in degrees.
   *
   * Positive values steer right, negative values steer left.
   * Use `0` to return to straight.
   *
   * @param angleDeg - Target angle in degrees (e.g. 20 = right, -20 = left, 0 = straight)
   * @param animated - true (default) = lerp over steeringDurationMs; false = instant
   */
  setSteeringAngle(angleDeg: number, animated = true): void {
    const targetRad = angleDeg * DEG_TO_RAD;
    this._targetAngleRad = targetRad;

    if (!animated) {
      this._currentAngleRad = targetRad;
      this._steeringProgress = 1;
      for (const node of this._steeringNodes) {
        node.rotation.y = targetRad;
      }
    } else {
      this._steeringStartAngle = this._currentAngleRad;
      this._steeringProgress = 0;
    }

    this.onNeedsRender?.();
  }

  /**
   * Steer to a discrete position.
   *
   * @param position - 'left' | 'straight' | 'right'
   * @param animated - true (default) = lerp; false = instant
   */
  setSteering(position: SteeringPosition, animated = true): void {
    this._steeringPosition = position;

    const targetRad =
      position === 'left'
        ? -this._steeringAngleRad
        : position === 'right'
          ? this._steeringAngleRad
          : 0;

    this._targetAngleRad = targetRad;

    if (!animated) {
      this._currentAngleRad = targetRad;
      this._steeringProgress = 1;
      for (const node of this._steeringNodes) {
        node.rotation.y = targetRad;
      }
    } else {
      this._steeringStartAngle = this._currentAngleRad;
      this._steeringProgress = 0;
    }

    this.onChanged?.({ type: 'steering', steeringPosition: position, animated });
    this.onNeedsRender?.();
  }

  /** Snapshot of the current wheel system state. */
  getState(): WheelState {
    return {
      isRotating: this._isRotating,
      speedFactor: this._currentSpeedFactor,
      steeringPosition: this._steeringPosition,
      steeringAngleDeg: this._currentAngleRad / DEG_TO_RAD,
    };
  }

  /** True while the wheel system is animating (spin ramp or steering lerp). */
  get isActive(): boolean {
    return (
      this._currentSpeedFactor > 0 ||
      this._currentSpeedFactor !== this._targetSpeedFactor ||
      this._steeringProgress < 1
    );
  }

  /**
   * Scan an external GLB scene for steering nodes and absorb them.
   *
   * Called when a new rim GLB mounts so the swapped wheels also steer in sync.
   * Immediately snaps found nodes to the current steering angle.
   * Safe to call repeatedly (deduplicates by reference). No-op before init().
   */
  scanAdditionalScene(scene: RevealObject): void {
    if (!this._initialized || this._steeringNodeNames.length === 0) return;
    const names = new Set(this._steeringNodeNames);
    // RevealObject.traverse callback type is RevealMesh, but at runtime these
    // are full three.js Object3D nodes — cast to access .name and .rotation.
    scene.traverse((child) => {
      const node = child as unknown as Object3D;
      if (names.has(node.name) && !this._steeringNodes.includes(node)) {
        this._steeringNodes.push(node);
        node.rotation.y = this._currentAngleRad;
      }
    });
    if (this._steeringNodes.length > 0) this.onNeedsRender?.();
  }

  /**
   * Reset steering to straight, clear node references.
   * Does NOT reset the spin angle (rotation.x) — that would create a jarring snap.
   */
  dispose(): void {
    // Return steering to neutral
    for (const node of this._steeringNodes) {
      node.rotation.y = 0;
    }
    this._rotationNodes = [];
    this._steeringNodes = [];
    this._initialized = false;
    this._isRotating = false;
    this._currentSpeedFactor = 0;
    this._targetSpeedFactor = 0;
    this._steeringProgress = 1;
    this._currentAngleRad = 0;
    this._targetAngleRad = 0;
    this._steeringPosition = 'straight';
  }
}
